import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneCall, ListTodo } from "lucide-react";

const PRIORITIES = ["low", "medium", "high"];

function RepSelect({ value, onChange, reps, testId }) {
  return (
    <div><Label>Assign to</Label>
      <Select value={value || "me"} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="me">Myself</SelectItem>{reps.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function PrioritySelect({ value, onChange, testId }) {
  return (
    <div><Label>Priority</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

export function QuickFollowUpDialog({ open, onOpenChange, date, defaultRep, facilities, reps, isAdmin, onSaved }) {
  const blank = { facility_id: "", title: "", due_date: date, priority: "medium", assigned_to: defaultRep || "me", notes: "" };
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF({ ...blank, due_date: date, assigned_to: defaultRep || "me" }); /* eslint-disable-next-line */ }, [open, date, defaultRep]);
  const save = async () => {
    if (!f.facility_id) { toast.error("Pick a facility"); return; }
    if (!f.title.trim()) { toast.error("Give the follow-up a title"); return; }
    try {
      await api.post("/followups", { facility_id: f.facility_id, title: f.title.trim(), due_date: f.due_date, priority: f.priority, notes: f.notes || null, assigned_to: isAdmin && f.assigned_to !== "me" ? f.assigned_to : null });
      toast.success("Follow-up added to the plan"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-followup-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PhoneCall className="w-5 h-5 text-orange-600" /> New follow-up</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Facility</Label>
            <Select value={f.facility_id} onValueChange={v => setF({ ...f, facility_id: v })}>
              <SelectTrigger data-testid="qf-facility"><SelectValue placeholder="Pick a facility" /></SelectTrigger>
              <SelectContent className="max-h-72">{facilities.map(x => <SelectItem key={x.facility_id} value={x.facility_id}>{x.name} · {x.city}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Title</Label><Input data-testid="qf-title" placeholder="Call DON about referral packet" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Due date</Label><Input type="date" data-testid="qf-date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} /></div>
            <PrioritySelect value={f.priority} onChange={v => setF({ ...f, priority: v })} testId="qf-priority" />
          </div>
          {isAdmin && <RepSelect value={f.assigned_to} onChange={v => setF({ ...f, assigned_to: v })} reps={reps} testId="qf-rep" />}
          <div><Label>Notes</Label><Textarea rows={2} data-testid="qf-notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="qf-save">Add follow-up</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickTaskDialog({ open, onOpenChange, date, defaultRep, facilities, reps, isAdmin, onSaved }) {
  const blank = { title: "", due_date: date, due_time: "09:00", priority: "medium", assigned_to: defaultRep || "me", facility_id: "none", description: "" };
  const [t, setT] = useState(blank);
  useEffect(() => { if (open) setT({ ...blank, due_date: date, assigned_to: defaultRep || "me" }); /* eslint-disable-next-line */ }, [open, date, defaultRep]);
  const save = async () => {
    if (!t.title.trim()) { toast.error("Give the task a title"); return; }
    try {
      await api.post("/tasks", { title: t.title.trim(), due_at: `${t.due_date}T${t.due_time || "09:00"}:00`, priority: t.priority, description: t.description || null, facility_id: t.facility_id === "none" ? null : t.facility_id, assigned_to: isAdmin && t.assigned_to !== "me" ? t.assigned_to : null });
      toast.success("Task added to the plan"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-task-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListTodo className="w-5 h-5 text-green-700" /> New task</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Title</Label><Input data-testid="qt-title" placeholder="Prepare referral packets" value={t.title} onChange={e => setT({ ...t, title: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Due date</Label><Input type="date" data-testid="qt-date" value={t.due_date} onChange={e => setT({ ...t, due_date: e.target.value })} /></div>
            <div><Label>Time</Label><Input type="time" data-testid="qt-time" value={t.due_time} onChange={e => setT({ ...t, due_time: e.target.value })} /></div>
            <PrioritySelect value={t.priority} onChange={v => setT({ ...t, priority: v })} testId="qt-priority" />
          </div>
          <div><Label>Facility <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Select value={t.facility_id} onValueChange={v => setT({ ...t, facility_id: v })}>
              <SelectTrigger data-testid="qt-facility"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="none">— None —</SelectItem>{facilities.map(x => <SelectItem key={x.facility_id} value={x.facility_id}>{x.name} · {x.city}</SelectItem>)}</SelectContent>
            </Select></div>
          {isAdmin && <RepSelect value={t.assigned_to} onChange={v => setT({ ...t, assigned_to: v })} reps={reps} testId="qt-rep" />}
          <div><Label>Details</Label><Textarea rows={2} data-testid="qt-desc" value={t.description} onChange={e => setT({ ...t, description: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} className="bg-green-700 hover:bg-green-800" data-testid="qt-save">Add task</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
